import { useState, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField, IconButton, Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SettingsIcon from '@material-ui/icons/Settings';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import BusinessIcon from '@material-ui/icons/Business';
import RoomIcon from '@material-ui/icons/Room';
import axios from 'axios';
import SetupStatusBadge from './SetupStatusBadge';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    gap: 16,
    minHeight: 60,
    flexShrink: 0,
  },
  pickers: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
    maxWidth: 720,
  },
  picker: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fafafa',
      fontSize: '0.85rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 8px',
    },
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  settingsBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: '#f5f5f5',
    '&:hover': { background: '#eeeeee' },
  },
  optionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  optionName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
  },
  optionMeta: {
    fontSize: '0.7rem',
    color: '#999',
  },
  iconAdornment: {
    color: '#999',
    marginLeft: 4,
    fontSize: 18,
  },
  optionRowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  aspireChip: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.4,
    padding: '1px 6px',
    background: '#FFF4E5',
    color: '#B45309',
    border: '1px solid #FDBA74',
    borderRadius: 3,
    textTransform: 'uppercase',
  },
}));

/**
 * Header with horizontal Client + Jobsite autocompletes + setup badge + settings button
 */
const SiteTakeoffHeader = ({
  url,
  clients = [],
  selectedClient,
  selectedJobsite,
  onClientChange,
  onJobsiteChange,
  setupComplete,
  onSettingsClick,
  onGuideClick,
}) => {
  const classes = useStyles();

  // ===== Client search (filter Redux clients list) =====
  const [clientQuery, setClientQuery] = useState('');

  const clientOptions = useMemo(() => {
    if (!clients?.length) return [];
    const getName = (c) => (c.displayName || c.searchName || c.companyName || c.name || '');
    // Empty / very short query: show the first 30 clients alphabetically so the
    // caret reveals the available list instead of an empty dropdown.
    if (!clientQuery || clientQuery.trim().length < 2) {
      return [...clients]
        .sort((a, b) => getName(a).localeCompare(getName(b)))
        .slice(0, 30);
    }
    const q = clientQuery.toLowerCase();
    return clients.filter(c => getName(c).toLowerCase().includes(q)).slice(0, 30);
  }, [clients, clientQuery]);

  // ===== Jobsite search =====
  const [jobsiteOptions, setJobsiteOptions] = useState([]);
  const [jobsiteLoading, setJobsiteLoading] = useState(false);
  const [jobsiteQuery, setJobsiteQuery] = useState('');

  // One-shot flag: arm when the client actually changes so the next jobsite
  // fetch auto-selects the first result. Cleared after firing (or skipping)
  // so that clearing the jobsite later does NOT re-trigger auto-select.
  const autoSelectArmedRef = useRef(false);
  const prevClientIdRef = useRef(null);
  useEffect(() => {
    const id = selectedClient?._id || null;
    if (id !== prevClientIdRef.current) {
      autoSelectArmedRef.current = !!id;
      prevClientIdRef.current = id;
    }
  }, [selectedClient]);

  useEffect(() => {
    if (!url) return;
    // Allow empty query when a client is selected (show all their sites)
    if (!selectedClient && jobsiteQuery.trim().length < 1) {
      setJobsiteOptions([]);
      return;
    }
    let cancelled = false;
    setJobsiteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = { q: jobsiteQuery };
        if (selectedClient?._id) params.client = selectedClient._id;
        const resp = await axios.get(`${url}/api/customer/jobsites/search`, { params });
        if (!cancelled) {
          const sites = resp.data?.jobsites || [];
          setJobsiteOptions(sites);
          // Auto-select the first jobsite ONLY when the user just changed clients
          // (not when they clear the jobsite picker). Armed by the prevClientId
          // effect above; fires once per client change.
          if (
            autoSelectArmedRef.current
            && selectedClient?._id
            && !jobsiteQuery.trim()
            && sites.length > 0
          ) {
            autoSelectArmedRef.current = false;
            onJobsiteChange(sites[0]);
          } else if (autoSelectArmedRef.current && sites.length === 0) {
            // Client has no sites — disarm so we don't fire later on a stray re-fetch.
            autoSelectArmedRef.current = false;
          }
        }
      } catch (err) {
        if (!cancelled) setJobsiteOptions([]);
      } finally {
        if (!cancelled) setJobsiteLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobsiteQuery, selectedClient, url]); // onJobsiteChange intentionally excluded

  return (
    <div className={classes.header}>
      <div className={classes.pickers}>
        <div data-tour="site-takeoff-client-picker" style={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            className={classes.picker}
            size="small"
            options={clientOptions}
            value={selectedClient || null}
            onChange={(_, val) => onClientChange(val)}
            onInputChange={(_, val) => setClientQuery(val)}
            getOptionLabel={(opt) => opt?.displayName || opt?.searchName || opt?.companyName || opt?.name || ''}
            getOptionSelected={(opt, val) => opt?._id === val?._id}
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div className={classes.optionRow}>
                <span className={classes.optionName}>{opt.displayName || opt.searchName || opt.companyName || opt.name}</span>
                {opt.address && <span className={classes.optionMeta}>{opt.address}</span>}
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search client..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <BusinessIcon className={classes.iconAdornment} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>

        <div data-tour="site-takeoff-jobsite-picker" style={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            className={classes.picker}
            size="small"
            options={jobsiteOptions}
            loading={jobsiteLoading}
            value={selectedJobsite || null}
            onChange={(_, val) => onJobsiteChange(val)}
            onInputChange={(_, val) => setJobsiteQuery(val)}
            getOptionLabel={(opt) => {
              if (!opt) return '';
              const name = opt.name || '';
              const addr = opt.address || '';
              return name && addr ? `${name} — ${addr}` : (name || addr);
            }}
            getOptionSelected={(opt, val) => opt?._id === val?._id}
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div className={classes.optionRow}>
                <div className={classes.optionRowTop}>
                  <span className={classes.optionName}>{opt.name || 'Unnamed'}</span>
                  {opt.source === 'aspire' && (
                    <span className={classes.aspireChip} title="Not yet imported — picking will create a local jobsite from Aspire">
                      Aspire
                    </span>
                  )}
                </div>
                <span className={classes.optionMeta}>
                  {[opt.address, opt.city, opt.state].filter(Boolean).join(', ')}
                  {opt.client?.companyName ? ` · ${opt.client.companyName}` : ''}
                  {opt.source === 'aspire' && opt.aspirePropertyId ? ` · Aspire #${opt.aspirePropertyId}` : ''}
                </span>
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedClient ? 'Select jobsite' : 'Search any jobsite...'}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <RoomIcon className={classes.iconAdornment} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.rightSide}>
        {selectedJobsite && (
          <div data-tour="site-takeoff-setup-status">
            <SetupStatusBadge complete={setupComplete} />
          </div>
        )}
        <Tooltip title="Show guide">
          <IconButton className={classes.settingsBtn} onClick={onGuideClick} size="small">
            <HelpOutlineIcon style={{ fontSize: 20, color: '#666' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Property settings">
          <IconButton
            className={classes.settingsBtn}
            onClick={onSettingsClick}
            size="small"
            data-tour="site-takeoff-settings-button"
          >
            <SettingsIcon style={{ fontSize: 20, color: '#666' }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default SiteTakeoffHeader;
